import { createApp } from "vue";
import App from "./App.vue";
import { createPinia } from "pinia";
import "./assets/scss/index.scss";
import router from "./router";
import type { OktaVueOptions } from "@okta/okta-vue";
import OktaVue from "@okta/okta-vue";
import { OktaAuth } from "@okta/okta-auth-js";

import { useLogger } from "./composables/logger";
import registerServiceWorker from "@/composables/pwa/registerServiceWorker";
import { registerSentry } from "@/plugins/sentryPlugin";

import Highcharts from "highcharts/highstock";
import HighchartsVue from "highcharts-vue";

import VueJsonPretty from "vue-json-pretty";
import "vue-json-pretty/lib/styles.css";

import { registerPrimevue } from "./primevue";
import { registerOkta } from "./plugins/oktaPlugin";
import { FontAwesomeIcon } from "./fontawesome";
import { registerConfirmation } from "./plugins/confirmationPlugin";
import { formPlugin } from "./plugins/piniaForm";
import calculationWorkerPlugin from "./plugins/calculationWorkerPlugin";
import piniaPluginPersistedstate from "pinia-plugin-persistedstate";

const { info } = useLogger();

const pinia = createPinia();
pinia.use(formPlugin);
pinia.use(piniaPluginPersistedstate);

if (import.meta.env.MODE === "development") {
  const apiUrl = import.meta.env.VITE_APP_API_URL;
  const apiPort = apiUrl.split(":")[1];

  window.$pinia = pinia;
  window.config = {
    GRAPHQL_URL: `${apiPort ? "http" : "https"}://${apiUrl}/v1/graphql`,
    GRAPHQL_WS_ENDPOINT: `${apiPort ? "ws" : "wss"}://${apiUrl}/v1/graphql`,
    OKTA_AUTH_REFRESH_MINUTES: import.meta.env
      .VITE_APP_OKTA_AUTH_REFRESH_MINUTES,
    OKTA_ISSUER: import.meta.env.VITE_APP_OKTA_ISSUER,
    OKTA_CLIENT_ID: import.meta.env.VITE_APP_OKTA_CLIENT_ID,
    OKTA_LOGGING: import.meta.env.VITE_APP_OKTA_LOGGING === "true",
    ENV: "development",
    IS_SENTRY_ENABLED: "false",
    SENTRY_ENV: "",
    SENTRY_DSN: "",
    SUMMIT_KNOWLEDGE_URL: import.meta.env.VITE_SUMMIT_KNOWLEDGE_URL
  };
}

const oktaIssuer =
  import.meta.env.MODE === "development"
    ? (import.meta.env.VITE_APP_OKTA_ISSUER as string)
    : window.config.OKTA_ISSUER;

const oktaClientId =
  import.meta.env.MODE === "development"
    ? (import.meta.env.VITE_APP_OKTA_CLIENT_ID as string)
    : window.config.OKTA_CLIENT_ID;

export const oktaAuth = new OktaAuth({
  issuer: oktaIssuer,
  clientId: oktaClientId,
  redirectUri: window.location.origin + "/login/callback",
  scopes: ["openid", "profile", "email", "offline_access"],
  devMode: import.meta.env.MODE !== "production" && window.config.OKTA_LOGGING,
  tokenManager: {
    autoRemove: false,
    autoRenew: false
  }
});

info({ env: import.meta.env });
info({ window: window.config });
const app = createApp(App)
  .use(pinia)
  .use(router)
  .use(registerPrimevue)
  .use(registerOkta)
  .use(registerConfirmation)
  .use(OktaVue, { oktaAuth } as OktaVueOptions)
  .use(registerServiceWorker)
  .use(registerSentry)
  .use(HighchartsVue, { highcharts: Highcharts })
  .use(calculationWorkerPlugin, {
    maxWorkers: Math.ceil(navigator.hardwareConcurrency / 2)
  })
  .component("VueJsonPretty", VueJsonPretty)
  .component("icon", FontAwesomeIcon);

app.mount("#app");
